import { Box, Textarea } from '@mantine/core';
import { useState } from 'react';
import { DateInputWithLabel } from 'src/design-system/DateInputWithLabel';
import { FIELD_STYLES, InputWithLabel } from 'src/design-system/InputWithLabel';
import { Select } from 'src/design-system/Select';
import { MoneyInput } from './MoneyInput';

type SharedFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
};

export type CashflowField =
  | (SharedFieldProps & {
      kind: 'select';
      data: readonly { value: string; label: string }[];
      placeholder?: string;
      searchable?: boolean;
    })
  | (SharedFieldProps & { kind: 'date' })
  | (SharedFieldProps & {
      kind: 'text';
      /** Free text that can run to a few sentences, given a box that grows with it. */
      multiline?: boolean;
    })
  | (Omit<SharedFieldProps, 'value' | 'onChange'> & {
      kind: 'money';
      value: number | '';
      onChange: (value: number | '') => void;
    });

// The right-hand column, for when and how much: frequency or date over the amount. Wide enough
// for the longest date the picker writes ("20 September 2000") beside its calendar icon and
// clear button.
const SIDE_COLUMN_WIDTH = 220;

/**
 * The rows are the same in every form: what the item is on the left, and when and how much in a
 * fixed column on the right, so each kind of field sits in the same place whichever form is
 * open. A row of one spans both.
 *
 * A required field left empty says what it needs under itself, since save stays disabled until it
 * is filled and would otherwise not say why. On a new item that waits until the adviser has
 * changed something, so a fresh form does not open covered in errors; `revealRequired` shows them
 * at once, for an existing item that arrived with a required field missing.
 */
export function CashflowFields({
  rows,
  revealRequired = false,
  onAnyChange,
}: {
  rows: CashflowField[][];
  revealRequired?: boolean;
  onAnyChange?: () => void;
}) {
  const [changed, setChanged] = useState(false);
  const requiredError = (field: CashflowField) =>
    (revealRequired || changed) && field.required && isEmpty(field.value)
      ? requiredMessage(field)
      : undefined;
  const handleChange = () => {
    setChanged(true);
    onAnyChange?.();
  };
  return (
    <>
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          style={{
            display: 'grid',
            gridTemplateColumns:
              row.length > 1 ? `minmax(0, 1fr) ${SIDE_COLUMN_WIDTH}px` : 'minmax(0, 1fr)',
            columnGap: 'var(--mantine-spacing-md)',
            alignItems: 'start',
          }}
        >
          {row.map((field, fieldIndex) => (
            <CashflowFieldInput
              key={field.label}
              field={field}
              error={requiredError(field)}
              onChanged={handleChange}
              // The modal's focus trap lands on the first element it can, which is the header's
              // close button; the form opens on its first field instead.
              autoFocus={rowIndex === 0 && fieldIndex === 0}
            />
          ))}
        </Box>
      ))}
    </>
  );
}

function CashflowFieldInput({
  field,
  autoFocus,
  error,
  onChanged,
}: {
  field: CashflowField;
  autoFocus: boolean;
  error?: string;
  onChanged: () => void;
}) {
  const changing =
    <T,>(onChange: (value: T) => void) =>
    (value: T) => {
      onChanged();
      onChange(value);
    };
  const shared = {
    label: field.label,
    required: field.required,
    disabled: field.disabled,
    'data-autofocus': autoFocus || undefined,
  };

  if (field.kind === 'select') {
    return (
      <Select
        {...shared}
        data={field.data as { value: string; label: string }[]}
        value={field.value || null}
        onChange={value => changing(field.onChange)(value ?? '')}
        placeholder={field.placeholder}
        searchable={field.searchable}
        error={error}
        // A searchable Select opens its list on focus, and the form opens on its first field, so
        // it would open with the list already down. Clicking, typing and arrowing still open it.
        openOnFocus={false}
        allowDeselect={false}
      />
    );
  }

  if (field.kind === 'date') {
    return (
      <DateInputWithLabel
        {...shared}
        value={field.value || null}
        onChange={value => changing(field.onChange)(value ?? '')}
        placeholder="DD/MM/YYYY"
        error={error}
      />
    );
  }

  if (field.kind === 'money') {
    return <MoneyInput {...shared} value={field.value} onChange={changing(field.onChange)} />;
  }

  if (field.multiline) {
    return (
      <Textarea
        {...shared}
        value={field.value}
        onChange={event => changing(field.onChange)(event.currentTarget.value)}
        error={error}
        autosize
        minRows={3}
        maxRows={8}
        styles={FIELD_STYLES}
      />
    );
  }

  return (
    <InputWithLabel
      {...shared}
      value={field.value}
      onChange={event => changing(field.onChange)(event.currentTarget.value)}
      error={error}
    />
  );
}

function isEmpty(value: string | number | ''): boolean {
  return typeof value === 'string' && value.trim() === '';
}

/** What every required field left empty needs, for the save button to say why it is disabled. */
export function missingRequired(rows: CashflowField[][]): string[] {
  return rows
    .flat()
    .filter(field => field.required && isEmpty(field.value))
    .map(requiredMessage);
}

/** Says what a field needs in the words its control asks for: a choice, or something typed. */
function requiredMessage(field: CashflowField): string {
  const noun = field.label.toLowerCase();
  return field.kind === 'text' ? `Enter a ${noun}` : `Choose a ${noun}`;
}
